import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  GitBranch,
  Pencil,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import {
  useCreateFlow,
  useDeleteFlow,
  useFlows,
  usePublishFlow,
  useValidateFlow,
} from "@/features/flows/hooks";
import type { Flow } from "@/features/flows/api";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<string, string> = {
  published:
    "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  archived:
    "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "text-muted-foreground bg-muted";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
        cls,
      )}
    >
      {status}
    </span>
  );
}

export default function FlowsPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);

  const q = useFlows();
  const flows = q.data ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Flow | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
            <GitBranch className="size-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Conversation Flows
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Visual flow builder for multi-step agent conversations.
            </p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            New Flow
          </Button>
        )}
      </div>

      {q.isLoading && (
        <Card>
          <CardContent className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && flows.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <GitBranch className="size-10 text-muted-foreground" />
            <div>
              <p className="text-base font-medium">No flows yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Build a flow to visually define how your agent handles
                conversations.
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" />
                Create your first flow
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!q.isLoading && flows.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Version</th>
                    <th className="px-4 py-3 font-medium">Validation</th>
                    <th className="px-4 py-3 font-medium">Last updated</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {flows.map((f) => (
                    <FlowRow
                      key={f.id}
                      flow={f}
                      canEdit={canEdit}
                      onOpen={() => navigate(`/flows/${f.id}`)}
                      onDelete={() => setDeleteTarget(f)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <CreateFlowDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/flows/${id}`)}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete flow</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{" "}
              <span className="font-medium text-foreground">
                {deleteTarget?.name}
              </span>
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <DeleteAction
              target={deleteTarget}
              onDone={() => setDeleteTarget(null)}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FlowRow({
  flow,
  canEdit,
  onOpen,
  onDelete,
}: {
  flow: Flow;
  canEdit: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const validate = useValidateFlow(flow.id);
  const publish = usePublishFlow(flow.id);

  async function onValidate(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const res = await validate.mutateAsync();
      if (res.is_valid) {
        toast.success("Flow is valid");
      } else {
        toast.warning(
          res.errors?.slice(0, 2).join("; ") || "Validation failed",
        );
      }
    } catch {
      toast.error("Validation error");
    }
  }

  async function onPublish(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await publish.mutateAsync();
      toast.success("Flow published");
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(
        e?.response?.data?.error || "Failed to publish — validate first",
      );
    }
  }

  return (
    <tr className="cursor-pointer hover:bg-accent/40" onClick={onOpen}>
      <td className="px-4 py-3">
        <p className="font-medium">{flow.name}</p>
        {flow.description && (
          <p className="text-xs text-muted-foreground">{flow.description}</p>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={flow.status} />
      </td>
      <td className="px-4 py-3 text-muted-foreground">v{flow.version}</td>
      <td className="px-4 py-3">
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
            flow.is_valid
              ? "border-emerald-200 text-emerald-700 dark:border-emerald-900 dark:text-emerald-400"
              : "border-input text-muted-foreground",
          )}
        >
          {flow.is_valid ? "valid" : "not validated"}
        </span>
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {new Date(flow.updated_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onOpen}
            aria-label="Open editor"
          >
            <Pencil className="size-4" />
          </Button>
          {canEdit && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-blue-600 hover:text-blue-600 dark:text-blue-400"
                onClick={onValidate}
                disabled={validate.isPending}
                aria-label="Validate graph"
              >
                <CheckCircle2 className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-emerald-600 hover:text-emerald-600 dark:text-emerald-400"
                onClick={onPublish}
                disabled={
                  !flow.is_valid ||
                  flow.status === "published" ||
                  publish.isPending
                }
                aria-label="Publish"
              >
                <Send className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                aria-label="Delete"
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function DeleteAction({
  target,
  onDone,
}: {
  target: Flow | null;
  onDone: () => void;
}) {
  const remove = useDeleteFlow();
  async function confirm() {
    if (!target) return;
    try {
      await remove.mutateAsync(target.id);
      toast.success("Flow deleted");
      onDone();
    } catch {
      toast.error("Failed to delete flow");
    }
  }
  return (
    <AlertDialogAction
      onClick={(e) => {
        e.preventDefault();
        confirm();
      }}
      disabled={remove.isPending}
      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
    >
      {remove.isPending ? "Deleting…" : "Delete"}
    </AlertDialogAction>
  );
}

function CreateFlowDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateFlow();

  function reset() {
    setName("");
    setDescription("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const f = await create.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success("Flow created");
      reset();
      onOpenChange(false);
      onCreated(f.id);
    } catch {
      toast.error("Failed to create flow");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Flow</DialogTitle>
          <DialogDescription>
            You&apos;ll be taken straight to the editor.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="flow-name">Flow Name</Label>
            <Input
              id="flow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="flow-desc">Description</Label>
            <Textarea
              id="flow-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? "Creating…" : "Create & Open Editor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
